import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import { api } from "@/lib/api";
import { GAME_BY_ID, GAMES } from "@/lib/games";
import { ArrowLeft } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";

export default function ChildDetail() {
  const { id } = useParams();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, sess] = await Promise.all([
        api.get(`/stats/child/${id}`),
        api.get("/sessions", { params: { child_id: id } }),
      ]);
      setStats(s.data);
      setSessions(sess.data);
    })();
  }, [id]);

  if (!stats) {
    return (
      <DashboardLayout>
        <div className="text-slate-500">Carregando relatório…</div>
      </DashboardLayout>
    );
  }

  const child = stats.child;
  const byGameData = GAMES.map((g) => {
    const entry = stats.by_game.find((e) => e.game_id === g.id);
    return {
      name: g.name.split(" ")[0],
      score: entry?.avg_score || 0,
      plays: entry?.plays || 0,
      color: g.color,
    };
  });

  const timeline = [...sessions]
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .map((s, i) => ({
      idx: i + 1,
      score: s.score,
      label: GAME_BY_ID[s.game_id]?.name?.split(" ")[0] || s.game_id,
      date: new Date(s.created_at).toLocaleDateString("pt-BR"),
    }));

  return (
    <DashboardLayout>
      <Link to="/dashboard/children" className="text-sm text-sky-600 inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Voltar
      </Link>
      <div className="flex items-center gap-4 mb-8">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center text-4xl"
          style={{ background: child.avatar_color }}
        >
          {child.avatar_emoji}
        </div>
        <div>
          <h1 className="text-3xl">{child.name}</h1>
          <p className="text-slate-500 text-sm">
            {child.age} anos · {child.coins} moedas · {stats.total_sessions} sessões
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="dash-card">
          <h3 className="text-base font-semibold mb-4">Pontuação média por jogo</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={byGameData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 12, fill: "#475569" }} />
                <Tooltip />
                <Bar dataKey="score" fill="#0284C7" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="dash-card">
          <h3 className="text-base font-semibold mb-4">Evolução das pontuações</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="idx" tick={{ fontSize: 12, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 12, fill: "#475569" }} />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="#F59E0B" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <h2 className="text-xl font-semibold mb-3">Desempenho por jogo</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {GAMES.map((g) => {
          const e = stats.by_game.find((x) => x.game_id === g.id);
          return (
            <div key={g.id} className="dash-card">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                  style={{ background: g.color + "22" }}
                >
                  {g.emoji}
                </div>
                <div>
                  <div className="font-semibold">{g.name}</div>
                  <div className="text-xs text-slate-500">{g.test}</div>
                </div>
              </div>
              {e ? (
                <div className="grid grid-cols-3 gap-2 text-sm mt-3">
                  <Metric label="Partidas" value={e.plays} />
                  <Metric label="Média" value={e.avg_score} />
                  <Metric label="Melhor" value={e.best_score} />
                  <Metric label="Acertos" value={e.total_hits} />
                  <Metric label="Erros" value={e.total_misses} />
                  <Metric
                    label="TR médio"
                    value={e.avg_reaction_ms ? `${Math.round(e.avg_reaction_ms)}ms` : "—"}
                  />
                </div>
              ) : (
                <div className="text-sm text-slate-400 mt-2">Sem dados ainda.</div>
              )}
            </div>
          );
        })}
      </div>

      <h2 className="text-xl font-semibold mb-3">Histórico de sessões</h2>
      <div className="dash-card overflow-x-auto">
        {sessions.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhuma sessão registrada.</p>
        ) : (
          <table className="w-full text-sm" data-testid="sessions-table">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-2 font-medium">Data</th>
                <th className="py-2 font-medium">Jogo</th>
                <th className="py-2 font-medium">Pontuação</th>
                <th className="py-2 font-medium">Acertos</th>
                <th className="py-2 font-medium">Erros</th>
                <th className="py-2 font-medium">Duração</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const g = GAME_BY_ID[s.game_id];
                const m = s.metrics || {};
                return (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="py-2">{new Date(s.created_at).toLocaleString("pt-BR")}</td>
                    <td className="py-2">
                      <span className="mr-2">{g?.emoji}</span>
                      {g?.name || s.game_id}
                    </td>
                    <td className="py-2 font-semibold">{s.score}</td>
                    <td className="py-2">{m.hits ?? "—"}</td>
                    <td className="py-2">{m.misses ?? "—"}</td>
                    <td className="py-2">{(s.duration_ms / 1000).toFixed(1)}s</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-semibold text-slate-900">{value}</div>
    </div>
  );
}
